'use client'

import React, { ReactNode } from 'react'

export interface CardSelectOption<TValue extends string | number = string | number> {
    label: string
    value: TValue
    icon?: ReactNode
}

interface CardSelectProps<TValue extends string | number = string | number> {
    options: CardSelectOption<TValue>[]
    value: TValue | null | undefined
    onChange: (value: TValue, option: CardSelectOption<TValue>) => void
    /** Tailwind grid class override (defaults to 1 col mobile / 2 col desktop) */
    containerClassName?: string
    /** data-test prefix for each card. Defaults to `form-select-option`,
     *  mirroring Vue's FormSelect.vue (`:data-test="`form-select-option-${value}`"`).
     *  Pass `select-card` for landing/family-type-style cards (Vue's SelectCard). */
    testIdPrefix?: string
    error?: { message?: string }
    /** Accent colour of the selected/hover state. Defaults to the historic blue. */
    variant?: 'blue' | 'travel'
    /** Travel variant only: "tile" centres the icon, label and check in a column. */
    layout?: 'row' | 'tile'
    /** Travel variant only: "sm" is the compact 64px icon row of the landing cards, "lg" gives icon-less rows the 60px desktop height. */
    size?: 'sm' | 'md' | 'lg'
    /** Travel variant only: "chevron" swaps the selection circle for a chevron, for lists whose pick acts as a link. */
    indicator?: 'check' | 'chevron'
}

const TravelCheck = ({ isSelected, compact = false, className = '' }: { isSelected: boolean; compact?: boolean; className?: string }) => (
    <span aria-hidden className={`flex size-6 shrink-0 items-center justify-center ${compact ? '' : 'md:size-[26px]'} ${className}`}>
        {isSelected ? (
            <span className={`flex size-5 items-center justify-center rounded-full bg-rm-blue ${compact ? '' : 'md:size-[22px]'}`}>
                <i className={`fa-regular fa-check text-[10px] text-white ${compact ? '' : 'md:text-[11px]'}`} />
            </span>
        ) : (
            <span className="size-full rounded-full border-2 border-[#DFD5D1] bg-white" />
        )}
    </span>
)

export default function CardSelect<TValue extends string | number = string | number>({
    options,
    value,
    onChange,
    containerClassName = 'grid grid-cols-1 sm:grid-cols-2 gap-2',
    testIdPrefix = 'form-select-option',
    error,
    variant = 'blue',
    layout = 'row',
    size = 'md',
    indicator = 'check',
}: CardSelectProps<TValue>) {
    const isTravel = variant === 'travel'
    const isTile = isTravel && layout === 'tile'
    const isCompact = isTravel && size === 'sm'
    // En erreur (aucun choix), les cartes prennent la bordure rouge des autres champs du formulaire
    const hasError = !!error?.message
    const selectedClass = isTravel
        ? 'rounded-[14px] border-2 border-rm-blue bg-rm-blue/10 text-pale'
        : 'rounded-rm-10 border border-blue-500 bg-rm-blue-pale text-pale'
    const idleClass = isTravel
        ? `rounded-[14px] border-2 bg-white text-pale ${hasError ? 'border-invalidRed' : 'border-[#EFE6E2] hover:border-rm-gray'}`
        : `rounded-rm-10 border bg-white text-pale ${hasError ? 'border-invalidRed' : 'border-gray-200 hover:border-blue-300'}`
    const layoutClass = (hasIcon: boolean) =>
        isTile
            ? 'flex flex-col items-center justify-center gap-2.5 px-3 pb-4 pt-3 text-center md:gap-3.5 md:rounded-2xl md:pb-6 md:pt-[22px]'
            : isTravel
                ? isCompact
                    ? 'flex min-h-[62px] items-center gap-2 px-3 py-2 text-left md:min-h-16'
                    : `flex items-center gap-3 px-4 text-left md:gap-3.5 md:px-[18px] ${hasIcon ? 'min-h-[84px] pl-3 md:min-h-[70px] md:pl-3.5' : `min-h-[52px] py-3 ${size === 'lg' ? 'md:min-h-[60px]' : ''}`}`
                : 'flex items-center min-h-12 h-[50px] pl-5 pr-3 text-left'

    return (
        <div>
            <div className={containerClassName}>
                {options.map((option) => {
                    const isSelected = option.value === value
                    const hasIcon = !!option.icon
                    return (
                        <button
                            type="button"
                            key={String(option.value)}
                            data-test={`${testIdPrefix}-${option.value}`}
                            onClick={() => onChange(option.value, option)}
                            className={`group transition-colors duration-200 cursor-pointer noselect focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue ${layoutClass(hasIcon)}
                                ${isSelected ? selectedClass : idleClass}`}
                            aria-pressed={isSelected}
                        >
                            {hasIcon && isTravel ? (
                                <span
                                    aria-hidden
                                    className={`flex shrink-0 items-center justify-center text-pale transition-colors duration-200 ${
                                        isTile
                                            ? 'size-[52px] rounded-full text-[21px] md:size-[72px] md:text-[28px]'
                                            : isCompact
                                                ? 'size-9 rounded-[11px] text-[17px]'
                                                : 'size-9 rounded-[11px] text-[18px] md:size-[38px] md:text-[20px]'
                                    } ${isSelected ? 'bg-white' : 'bg-neutral'}`}
                                >
                                    {option.icon}
                                </span>
                            ) : hasIcon ? (
                                <span
                                    className={`mr-3 text-[14px] leading-none flex items-center justify-center w-5 shrink-0 ${
                                        isSelected ? 'text-rm-blue-accent' : 'text-rm-gray-light'
                                    }`}
                                >
                                    {option.icon}
                                </span>
                            ) : null}
                            <span
                                className={
                                    isTravel
                                        ? `${isTile ? 'text-sm md:text-[15px]' : isCompact ? 'text-[15px] leading-tight md:text-sm' : 'text-[15px] sm:text-base'} ${isSelected ? 'font-bold' : 'font-normal'}`
                                        : 'text-[14px] font-medium'
                                }
                            >
                                {option.label}
                            </span>
                            {isTravel && indicator === 'chevron' ? (
                                <i
                                    aria-hidden
                                    className={`fa-regular fa-chevron-right ml-auto shrink-0 text-[15px] ${isSelected ? 'text-rm-blue' : 'text-pale'}`}
                                />
                            ) : isTravel ? (
                                <TravelCheck isSelected={isSelected} compact={isCompact} className={isTile ? '' : 'ml-auto'} />
                            ) : !hasIcon && (
                                <span
                                    aria-hidden
                                    className={`ml-auto flex size-5 shrink-0 items-center justify-center rounded-full ${
                                        isSelected ? 'bg-blue-500' : 'border-2 bg-white border-rm-gray-light'
                                    }`}
                                >
                                    {isSelected && <i className="fa-regular fa-check text-[9px] text-white" />}
                                </span>
                            )}
                        </button>
                    )
                })}
            </div>
            {error?.message && (
                <p className="mt-1 text-[12px] text-red-600">{error.message}</p>
            )}
        </div>
    )
}
